import React, { useState, useEffect, useRef } from 'react';
import {
  Trophy, Play, Pause, Square,
  X, ArrowRight, Volume2
} from 'lucide-react';
import useDragToScroll from '../../hooks/useDragToScroll';
import sfx from '../../utils/soundEffects';
import { speakFrench, stopSpeaking, pauseSpeaking } from '../../utils/voiceHelper';

const MASTERY_SPEECH_TEXT = `
Félicitations, citoyen !
Vous venez de réussir à faire fuir un agresseur avant la fin de son agression, tout en l'identifiant formellement, alors que vous aviez déjà débloqué toutes les fonctionnalités et atteint le nombre maximum de citoyens et de citoyens protecteurs.
Vous avez donc compris le véritable principe de MonAlerte : une protection basée sur la communauté et sur la technologie.
Plus il y a de citoyens protecteurs autour de vous, plus il est facile de faire fuir un agresseur.
Plus vous disposez de fonctionnalités de déclenchement d'alerte, plus vous pouvez alerter rapidement et récolter des preuves.
Et plus votre technologie de capture audio et vidéo est fiable, plus vous disposez de preuves solides pour faciliter une enquête, un procès, ou une acceptation auprès d'une assurance.
Cette simulation ne représente qu'une partie des fonctionnalités de MonAlerte, sans oublier l'application MonAlerte Enfant, entièrement dédiée à la protection de vos enfants.
Bravo, et continuez à faire de votre quartier un endroit plus sûr !
`.trim();

const MasteryCongratsModal = ({
  isOpen,
  onClose,
}) => {
  const containerRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const cardRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  // État Narration Vocale (même narrateur que la popup de bienvenue)
  const [ttsState, setTtsState] = useState('idle'); // 'idle' | 'playing' | 'paused'
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      setTtsState('idle');
      return;
    }

    sfx.playSuccess();

    // 1. Tentative de lecture du fichier audio HD (même narrateur que l'introduction)
    const audio = new Audio('/audio/mastery_congrats.m4a');
    audio.volume = 1.0;
    audio.onplay = () => setTtsState('playing');
    audio.onended = () => setTtsState('idle');
    audio.onerror = () => {
      playWebSpeechFallback();
    };
    audioRef.current = audio;

    const startAudioPlayback = () => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          setTtsState('playing');
        }).catch(() => {
          playWebSpeechFallback();
        });
      }
    };

    startAudioPlayback();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const playWebSpeechFallback = () => {
    speakFrench(MASTERY_SPEECH_TEXT, {
      onStart: () => setTtsState('playing'),
      onEnd: () => setTtsState('idle'),
      onError: () => setTtsState('idle'),
    });
  };

  const handlePlayTTS = () => {
    sfx.playClick();
    if (audioRef.current) {
      audioRef.current.play().then(() => {
        setTtsState('playing');
      }).catch(() => {
        playWebSpeechFallback();
      });
    } else {
      playWebSpeechFallback();
    }
  };

  const handlePauseTTS = () => {
    sfx.playClick();
    if (audioRef.current && ttsState === 'playing') {
      audioRef.current.pause();
      setTtsState('paused');
    } else if (ttsState === 'playing') {
      pauseSpeaking();
      setTtsState('paused');
    }
  };

  const handleStopTTS = () => {
    sfx.playClick();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    stopSpeaking();
    setTtsState('idle');
  };

  const handleClose = () => {
    sfx.playClick();
    handleStopTTS();
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="mastery-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3, 7, 18, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1120,
        borderRadius: '16px',
        padding: '10px',
        overflow: 'hidden',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.25s ease',
      }}
    >
      <style>{`
        @keyframes masteryPulseGlow {
          0% { box-shadow: 0 0 15px rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 0 40px rgba(16, 185, 129, 0.85); }
          100% { box-shadow: 0 0 15px rgba(16, 185, 129, 0.4); }
        }
        @keyframes masterySoundWave {
          0%, 100% { height: 4px; }
          50% { height: 18px; }
        }
      `}</style>

      <div
        ref={cardRef}
        style={{
          background: 'linear-gradient(165deg, rgba(6, 30, 24, 0.98) 0%, rgba(6, 13, 24, 0.99) 100%)',
          border: '2px solid #10b981',
          borderRadius: '16px',
          maxWidth: '780px',
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '20px 24px',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.95), 0 0 30px rgba(16, 185, 129, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          position: 'relative',
          animation: 'slideUpDuel 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards, masteryPulseGlow 2.4s ease-in-out infinite',
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: 'none',
            color: '#cbd5e1',
            borderRadius: '50%',
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={18} />
        </button>

        {/* EN-TÊTE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', borderBottom: '1.5px solid rgba(16, 185, 129, 0.3)', paddingBottom: '14px' }}>
          <div style={{
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.45) 100%)',
            border: '1.5px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
            flexShrink: 0,
          }}>
            <Trophy size={26} color="#4ade80" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.22rem', color: '#ffffff', fontWeight: 900, letterSpacing: '0.3px' }}>
              🏆 Félicitations, vous maîtrisez <span style={{ color: '#4ade80' }}>MonAlerte</span> !
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#cbd5e1', lineHeight: 1.4 }}>
              Toutes les fonctionnalités débloquées, communauté au maximum : suspect identifié ET agression interrompue !
            </p>
          </div>
        </div>

        {/* BARRE NARRATION VOCALE */}
        <div style={{
          background: 'rgba(5, 11, 20, 0.85)',
          border: '1.5px solid rgba(16, 185, 129, 0.35)',
          borderRadius: '12px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: '#4ade80', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Volume2 size={15} color="#4ade80" />
              🗣️ Narration Vocale :
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {ttsState !== 'playing' ? (
                <button
                  type="button"
                  onClick={handlePlayTTS}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '7px 14px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Play size={13} fill="currentColor" /> {ttsState === 'paused' ? 'Reprendre' : 'Écouter'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePauseTTS}
                  style={{
                    background: 'rgba(245, 158, 11, 0.25)',
                    color: '#ffd873',
                    border: '1px solid #f59e0b',
                    padding: '7px 14px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Pause size={13} /> Pause
                </button>
              )}

              {ttsState !== 'idle' && (
                <button
                  type="button"
                  onClick={handleStopTTS}
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#fca5a5',
                    border: '1px solid #ef4444',
                    padding: '7px 12px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Square size={12} fill="currentColor" /> Stop
                </button>
              )}
            </div>

            {ttsState === 'playing' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '20px', marginLeft: '6px' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <span
                    key={i}
                    style={{
                      width: '3px',
                      background: '#4ade80',
                      borderRadius: '2px',
                      animation: `masterySoundWave 0.6s infinite ease-in-out ${i * 0.12}s`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* TEXTE DU NARRATEUR */}
        <div style={{
          background: 'rgba(15, 39, 74, 0.5)',
          border: '1.2px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          fontSize: '0.82rem',
          color: '#e2e8f0',
          lineHeight: 1.6,
        }}>
          <p style={{ margin: '0 0 10px 0' }}>
            Vous venez de faire fuir un agresseur avant la fin de son agression, <strong style={{ color: '#4ade80' }}>tout en l'identifiant formellement</strong> — alors que vous aviez déjà débloqué toutes les fonctionnalités et atteint le maximum de citoyens et de citoyens protecteurs.
          </p>
          <p style={{ margin: '0 0 10px 0' }}>
            Vous avez compris le véritable principe de <strong>MonAlerte</strong> : une protection basée sur la <strong>communauté</strong> et sur la <strong>technologie</strong>.
          </p>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: '20px' }}>
            <li>👥 Plus il y a de <strong>citoyens protecteurs</strong>, plus il est facile de faire fuir un agresseur.</li>
            <li>⚡ Plus vous avez de <strong>fonctionnalités de déclenchement d'alerte</strong>, plus vous pouvez alerter rapidement et récolter des preuves.</li>
            <li>📷 Plus votre <strong>technologie de capture</strong> est fiable, plus vous disposez de preuves solides pour faciliter une enquête, un procès, ou une acceptation auprès d'une assurance.</li>
          </ul>
          <p style={{ margin: '0 0 10px 0' }}>
            Cette simulation ne représente qu'une partie des fonctionnalités de MonAlerte, sans oublier l'application <strong>MonAlerte Enfant</strong>, dédiée à la protection de vos enfants.
          </p>
          <p style={{ margin: 0, fontWeight: 700, color: '#4ade80' }}>
            Bravo, et continuez à faire de votre quartier un endroit plus sûr !
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 900,
              fontSize: '0.92rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.45)',
            }}
          >
            <span>Continuer l'aventure</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasteryCongratsModal;
